import { Star } from 'lucide-react'
import type { Product } from '@/types'
import { StarRatingDisplay } from '@/store/components/StarRating'
import { formatRatingValue } from '@/lib/productReview'
import { cn } from '@/lib/utils'

interface ProductRatingBadgeProps {
  product: Pick<Product, 'averageRating' | 'reviewCount'>
  className?: string
  showCount?: boolean
  /** Compact pill for product card image overlay. */
  variant?: 'default' | 'on-image' | 'home-star'
}

export function productHasStoreRating(
  product: Pick<Product, 'averageRating' | 'reviewCount'>
): boolean {
  return (product.reviewCount ?? 0) > 0 && (product.averageRating ?? 0) > 0
}

export function ProductRatingBadge({
  product,
  className,
  showCount = true,
  variant = 'default',
}: ProductRatingBadgeProps) {
  const count = product.reviewCount ?? 0
  const average = product.averageRating ?? 0
  if (count <= 0 || average <= 0) return null

  const ratingLabel = formatRatingValue(average)

  if (variant === 'home-star') {
    return (
      <span
        className={cn(
          'relative inline-flex h-8 w-8 shrink-0 items-center justify-center drop-shadow-md',
          className
        )}
        aria-label={`${ratingLabel} out of 5 stars`}
      >
        <Star
          className="h-8 w-8 fill-emerald-500 text-emerald-500"
          strokeWidth={1.25}
        />
        <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold leading-none text-white">
          {ratingLabel}
        </span>
      </span>
    )
  }

  if (variant === 'on-image') {
    return (
      <span
        className={cn(
          'inline-flex h-8 shrink-0 items-center gap-1 rounded-full border border-white/10 bg-black/50 px-2 backdrop-blur-sm',
          className
        )}
        aria-label={`${ratingLabel} out of 5 stars`}
      >
        <StarRatingDisplay rating={average} size="sm" showValue />
      </span>
    )
  }

  return (
    <div className={cn('inline-flex items-center gap-1.5', className)}>
      <StarRatingDisplay rating={average} size="sm" showValue />
      {showCount && (
        <span className="text-xs text-zinc-500">
          ({count} review{count === 1 ? '' : 's'})
        </span>
      )}
    </div>
  )
}
